/**
 * Email Logs Page
 * Displays raw email log data with detailed drill-down functionality
 * Shows exact records from the email_campaigns data source without grouping
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Mail,
  Eye,
  MousePointer,
  AlertTriangle,
  Send,
  CheckCircle,
  XCircle,
  Filter,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Download,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Target,
} from 'lucide-react';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '@services/api';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import { CustomAutocomplete } from '@components/analytics/CustomAutocomplete';

interface EmailLog {
  _id: string;
  messageId: string;
  from: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  eventType:
    | 'send'
    | 'delivery'
    | 'bounce'
    | 'complaint'
    | 'open'
    | 'click'
    | 'reject'
    | 'renderingFailure';
  status: 'sent' | 'delivered' | 'bounced' | 'complained' | 'opened' | 'clicked' | 'failed';
  timestamp: string;
  processingTimestamp?: string;
  campaignId?: string;
  tags?: Record<string, string>;
  userAgent?: string;
  ipAddress?: string;
  bounceType?: string;
  bounceSubType?: string;
  bouncedRecipients?: Array<{
    emailAddress: string;
    action?: string;
    status?: string;
    diagnosticCode?: string;
  }>;
  complainedRecipients?: Array<{
    emailAddress: string;
  }>;
  link?: string;
  rawData?: Record<string, any>;
  source: string;
  createdAt: string;
  updatedAt: string;
}

interface EmailLogResponse {
  emailLogs: EmailLog[];
  pagination: {
    total: number;
    limit: number;
    skip: number;
    hasMore: boolean;
  };
  meta?: {
    effectiveDateRange: {
      startDate: string;
      endDate: string;
    };
    originalDateRange: {
      startDate: string;
      endDate: string;
    };
    wasDateRangeLimited: boolean;
    warning?: string;
  };
}

interface EmailLogDetailsModalProps {
  emailLog: EmailLog | null;
  onClose: () => void;
}

// Modal component for showing full email log details
const EmailLogDetailsModal = ({ emailLog, onClose }: EmailLogDetailsModalProps) => {
  if (!emailLog) return null;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const renderJsonValue = (value: any, key?: string): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400">null</span>;
    }

    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-600' : 'text-red-600'}>{value.toString()}</span>;
    }

    if (typeof value === 'string') {
      // Special handling for dates
      if (key && (key.includes('timestamp') || key.includes('date') || key.includes('time'))) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return (
              <span className="text-blue-600">
                "{value}" <span className="text-gray-500 text-sm">({date.toLocaleString()})</span>
              </span>
            );
          }
        } catch {
          // Not a date, treat as regular string
        }
      }

      return <span className="text-green-600">"{value}"</span>;
    }

    if (typeof value === 'number') {
      return <span className="text-purple-600">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400">[]</span>;
      }
      return (
        <div className="ml-4">
          <div className="text-gray-600">[</div>
          {value.map((item, index) => (
            <div key={index} className="ml-4">
              <span className="text-gray-500">{index}:</span> {renderJsonValue(item)}
            </div>
          ))}
          <div className="text-gray-600">]</div>
        </div>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return <span className="text-gray-400">{'{}'}</span>;
      }
      return (
        <div className="ml-4">
          <div className="text-gray-600">{'{'}</div>
          {keys.map(objKey => (
            <div key={objKey} className="ml-4">
              <span className="text-blue-700 font-medium">"{objKey}"</span>
              <span className="text-gray-600">: </span>
              {renderJsonValue(value[objKey], objKey)}
            </div>
          ))}
          <div className="text-gray-600">{'}'}</div>
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-7xl sm:w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Email Log Details
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Message ID: {emailLog.messageId}
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 px-6 py-4 max-h-[70vh] overflow-y-auto">
            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Main Info */}
              <div className="space-y-6">
                {/* Email Message Details */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center mb-4">
                    <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Message Details
                    </h4>
                  </div>

                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        From:
                      </span>
                      <div className="col-span-2 space-y-1">
                        <span className="text-sm text-gray-900 dark:text-white break-all block">
                          {emailLog.from}
                        </span>
                        {emailLog.tags?.['ses:sender-identity'] &&
                          emailLog.tags['ses:sender-identity'] !== emailLog.from && (
                            <span className="text-xs text-blue-600 dark:text-blue-400 font-mono bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                              SES Identity: {emailLog.tags['ses:sender-identity']}
                            </span>
                          )}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        To:
                      </span>
                      <span className="col-span-2 text-sm text-gray-900 dark:text-white break-all">
                        {emailLog.to.join(', ')}
                      </span>
                    </div>

                    {emailLog.cc && emailLog.cc.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          CC:
                        </span>
                        <span className="col-span-2 text-sm text-gray-900 dark:text-white break-all">
                          {emailLog.cc.join(', ')}
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Subject:
                      </span>
                      <span className="col-span-2 text-sm text-gray-900 dark:text-white font-medium">
                        {emailLog.subject}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Message ID:
                      </span>
                      <span className="col-span-2 text-xs font-mono text-gray-700 dark:text-gray-300 break-all bg-white dark:bg-gray-800 p-2 rounded border">
                        {emailLog.messageId}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Event & Status */}
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 p-6 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center mb-4">
                    <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mr-2" />
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Event Information
                    </h4>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1">
                        Event Type
                      </span>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                          emailLog.eventType === 'open'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                            : emailLog.eventType === 'click'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                            : emailLog.eventType === 'bounce'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                            : emailLog.eventType === 'delivery'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                            : emailLog.eventType === 'send'
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                        }`}
                      >
                        {emailLog.eventType.toUpperCase()}
                      </span>
                    </div>

                    <div>
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400 block mb-1">
                        Status
                      </span>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                          emailLog.status === 'delivered'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                            : emailLog.status === 'opened'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                            : emailLog.status === 'clicked'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300'
                            : emailLog.status === 'bounced'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                            : emailLog.status === 'sent'
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                        }`}
                      >
                        {emailLog.status.toUpperCase()}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Event Time:
                      </span>
                      <span className="col-span-2 text-sm text-gray-900 dark:text-white font-mono">
                        {formatDate(emailLog.timestamp)}
                      </span>
                    </div>

                    {emailLog.processingTimestamp && (
                      <div className="grid grid-cols-3 gap-2">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          Processed:
                        </span>
                        <span className="col-span-2 text-sm text-gray-900 dark:text-white font-mono">
                          {formatDate(emailLog.processingTimestamp)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Campaign Information */}
                {emailLog.campaignId && (
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-6 rounded-lg border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center mb-4">
                      <Target className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Campaign Information
                      </h4>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Campaign ID:
                      </span>
                      <span className="col-span-2 text-sm font-mono text-gray-900 dark:text-white break-all bg-white dark:bg-gray-800 p-2 rounded border">
                        {emailLog.campaignId}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Technical Details */}
              <div className="space-y-6">
                {/* User Interaction Details */}
                {(emailLog.userAgent || emailLog.ipAddress || emailLog.link) && (
                  <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-6 rounded-lg border border-cyan-200 dark:border-cyan-800">
                    <div className="flex items-center mb-4">
                      <Eye className="w-5 h-5 text-cyan-600 dark:text-cyan-400 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        User Interaction
                      </h4>
                    </div>

                    <div className="space-y-3">
                      {emailLog.ipAddress && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            IP Address:
                          </span>
                          <span className="col-span-2 text-sm font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded border">
                            {emailLog.ipAddress}
                          </span>
                        </div>
                      )}

                      {emailLog.userAgent && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            User Agent:
                          </span>
                          <span className="col-span-2 text-xs text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded border break-all">
                            {emailLog.userAgent}
                          </span>
                        </div>
                      )}

                      {emailLog.link && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Clicked Link:
                          </span>
                          <div className="col-span-2">
                            <a
                              href={emailLog.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 dark:text-blue-400 hover:underline bg-white dark:bg-gray-800 p-2 rounded border block break-all"
                            >
                              {emailLog.link} <ExternalLink className="inline w-3 h-3 ml-1" />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Bounce/Complaint Details */}
                {(emailLog.bouncedRecipients?.length || emailLog.complainedRecipients?.length) && (
                  <div className="bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 p-6 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Delivery Issues
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {emailLog.bounceType && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Bounce Type:
                          </span>
                          <span className="col-span-2 text-sm font-medium text-red-700 dark:text-red-400">
                            {emailLog.bounceType}
                          </span>
                        </div>
                      )}

                      {emailLog.bounceSubType && (
                        <div className="grid grid-cols-3 gap-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            Sub-Type:
                          </span>
                          <span className="col-span-2 text-sm text-gray-900 dark:text-white">
                            {emailLog.bounceSubType}
                          </span>
                        </div>
                      )}

                      {emailLog.bouncedRecipients?.map((recipient, index) => (
                        <div
                          key={index}
                          className="bg-white dark:bg-gray-800 p-3 rounded border border-red-200 dark:border-red-800"
                        >
                          <div className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">
                            Bounced: {recipient.emailAddress}
                          </div>
                          {recipient.diagnosticCode && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 font-mono bg-gray-50 dark:bg-gray-700 p-2 rounded">
                              {recipient.diagnosticCode}
                            </div>
                          )}
                        </div>
                      ))}

                      {emailLog.complainedRecipients?.map((recipient, index) => (
                        <div
                          key={index}
                          className="bg-white dark:bg-gray-800 p-3 rounded border border-red-200 dark:border-red-800"
                        >
                          <div className="text-sm font-medium text-red-700 dark:text-red-400">
                            Complaint: {recipient.emailAddress}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* AWS SES Tags */}
                {emailLog.tags && Object.keys(emailLog.tags).length > 0 && (
                  <div className="bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-6 rounded-lg border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center mb-4">
                      <Target className="w-5 h-5 text-amber-600 dark:text-amber-400 mr-2" />
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        AWS SES Tags
                      </h4>
                    </div>

                    <div className="space-y-2">
                      {Object.entries(emailLog.tags).map(([key, value]) => (
                        <div key={key} className="grid grid-cols-3 gap-2">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 break-words">
                            {key}:
                          </span>
                          <span className="col-span-2 text-sm text-gray-900 dark:text-white font-mono bg-white dark:bg-gray-800 p-1 rounded text-xs break-all">
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Full Width Raw Data Section */}
            {emailLog.rawData && (
              <div className="mt-6">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/20 dark:to-gray-900/20 p-6 rounded-lg border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center mb-4">
                    <FileText className="w-5 h-5 text-slate-600 dark:text-slate-400 mr-2" />
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Complete Raw Data
                    </h4>
                    <span className="ml-2 px-2 py-1 bg-slate-200 dark:bg-slate-700 rounded text-xs text-slate-600 dark:text-slate-400">
                      AWS SES Event
                    </span>
                  </div>

                  <div className="bg-gray-900 dark:bg-black p-4 rounded-lg max-h-96 overflow-y-auto">
                    <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap">
                      {renderJsonValue(emailLog.rawData)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailLogsPage = () => {
  const { getSelection } = useDataSourceStore();
  const { companyId, dataSourceIds } = getSelection();

  const [selectedEmailLog, setSelectedEmailLog] = useState<EmailLog | null>(null);

  // Date range state (default: Last 7 days for better performance)
  const getDefaultDateRange = (): DateRange => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 7 days',
    };
  };

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const saved = localStorage.getItem('emailLogs_dateRange');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return getDefaultDateRange();
      }
    }
    return getDefaultDateRange();
  });

  const [showFilters, setShowFilters] = useState(() => {
    const saved = localStorage.getItem('emailLogs_showFilters');
    return saved ? JSON.parse(saved) : false;
  });

  // Filter states
  const [eventTypeFilter, setEventTypeFilter] = useState(() => {
    return localStorage.getItem('emailLogs_eventTypeFilter') || '';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('emailLogs_statusFilter') || '';
  });
  const [subjectFilter, setSubjectFilter] = useState(() => {
    return localStorage.getItem('emailLogs_subjectFilter') || '';
  });
  const [campaignIdFilter, setCampaignIdFilter] = useState(() => {
    return localStorage.getItem('emailLogs_campaignIdFilter') || '';
  });
  const [fromFilter, setFromFilter] = useState(() => {
    return localStorage.getItem('emailLogs_fromFilter') || '';
  });
  const [toFilter, setToFilter] = useState(() => {
    return localStorage.getItem('emailLogs_toFilter') || '';
  });
  const [ipAddressFilter, setIpAddressFilter] = useState(() => {
    return localStorage.getItem('emailLogs_ipAddressFilter') || '';
  });
  const [userAgentFilter, setUserAgentFilter] = useState(() => {
    return localStorage.getItem('emailLogs_userAgentFilter') || '';
  });
  const [configurationSetFilter, setConfigurationSetFilter] = useState(() => {
    return localStorage.getItem('emailLogs_configurationSetFilter') || '';
  });
  const [recipientIspFilter, setRecipientIspFilter] = useState(() => {
    return localStorage.getItem('emailLogs_recipientIspFilter') || '';
  });
  const [sourceIpFilter, setSourceIpFilter] = useState(() => {
    return localStorage.getItem('emailLogs_sourceIpFilter') || '';
  });
  const [messageIdFilter, setMessageIdFilter] = useState(() => {
    return localStorage.getItem('emailLogs_messageIdFilter') || '';
  });
  const [deviceClientFilter, setDeviceClientFilter] = useState(() => {
    return localStorage.getItem('emailLogs_deviceClientFilter') || '';
  });
  const [showDuplicates, setShowDuplicates] = useState(() => {
    // Clear any old localStorage setting and force default to false (deduplicated)
    localStorage.removeItem('emailLogs_showDuplicates');
    return false; // Always default to deduplicated view
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50); // Show 50 logs per page

  // Sorting state
  const [sortColumn, setSortColumn] = useState<keyof EmailLog>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem('emailLogs_dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem('emailLogs_showFilters', JSON.stringify(showFilters));
  }, [showFilters]);

  useEffect(() => {
    localStorage.setItem('emailLogs_eventTypeFilter', eventTypeFilter);
  }, [eventTypeFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_statusFilter', statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_subjectFilter', subjectFilter);
  }, [subjectFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_campaignIdFilter', campaignIdFilter);
  }, [campaignIdFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_fromFilter', fromFilter);
  }, [fromFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_toFilter', toFilter);
  }, [toFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_ipAddressFilter', ipAddressFilter);
  }, [ipAddressFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_userAgentFilter', userAgentFilter);
  }, [userAgentFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_configurationSetFilter', configurationSetFilter);
  }, [configurationSetFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_recipientIspFilter', recipientIspFilter);
  }, [recipientIspFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_sourceIpFilter', sourceIpFilter);
  }, [sourceIpFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_messageIdFilter', messageIdFilter);
  }, [messageIdFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_deviceClientFilter', deviceClientFilter);
  }, [deviceClientFilter]);

  useEffect(() => {
    localStorage.setItem('emailLogs_showDuplicates', JSON.stringify(showDuplicates));
  }, [showDuplicates]);

  // Fetch email logs
  const {
    data: emailLogsData,
    isLoading: loadingLogs,
    refetch: refetchLogs,
  } = useQuery({
    queryKey: [
      'email-logs',
      companyId,
      dataSourceIds,
      dateRange,
      eventTypeFilter,
      statusFilter,
      subjectFilter,
      campaignIdFilter,
      fromFilter,
      toFilter,
      ipAddressFilter,
      userAgentFilter,
      configurationSetFilter,
      recipientIspFilter,
      sourceIpFilter,
      messageIdFilter,
      deviceClientFilter,
      showDuplicates,
      currentPage,
      itemsPerPage,
    ],
    queryFn: async (): Promise<EmailLogResponse> => {
      const params: any = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
      };

      if (eventTypeFilter) params.eventType = eventTypeFilter;
      if (statusFilter) params.status = statusFilter;
      if (subjectFilter) params.subject = subjectFilter;
      if (campaignIdFilter) params.campaignId = campaignIdFilter;
      if (fromFilter) params.from = fromFilter;
      if (toFilter) params.to = toFilter;
      if (ipAddressFilter) params.ipAddress = ipAddressFilter;
      if (userAgentFilter) params.userAgent = userAgentFilter;
      if (configurationSetFilter) params.configurationSet = configurationSetFilter;
      if (recipientIspFilter) params.recipientIsp = recipientIspFilter;
      if (sourceIpFilter) params.sourceIp = sourceIpFilter;
      if (messageIdFilter) params.messageId = messageIdFilter;
      if (deviceClientFilter) params.deviceClient = deviceClientFilter;
      if (showDuplicates) params.showDuplicates = showDuplicates;

      console.log('📧 Email Logs API Request params:', params);
      const response = await api.get('/api/v1/aws-email-logs', { params });
      console.log('📧 Email Logs API Response:', response.data);
      return response.data.data;
    },
    enabled: !!companyId && dataSourceIds.length > 0,
  });

  const emailLogs = Array.isArray(emailLogsData?.emailLogs) ? emailLogsData.emailLogs : [];
  const pagination = emailLogsData?.pagination || {
    total: 0,
    limit: itemsPerPage,
    skip: 0,
    hasMore: false,
  };

  const totalPages = Math.ceil(pagination.total / itemsPerPage);

  // 🚀 SAFE AUTOCOMPLETE: Handle empty states gracefully to prevent crashes
  const extractUniqueValues = (
    logs: any[],
    extractor: (log: any) => any,
    validator?: (val: any) => boolean
  ) => {
    if (!Array.isArray(logs) || logs.length === 0) {
      return []; // Safe fallback for empty data
    }

    try {
      return Array.from(
        new Set(
          logs
            .filter(log => log && typeof log === 'object')
            .map(extractor)
            .filter(v => v && typeof v === 'string' && (validator ? validator(v) : v.length > 0))
        )
      )
        .sort()
        .slice(0, 100); // Limit to 100 for performance
    } catch (error) {
      console.warn('Autocomplete extraction error:', error);
      return [];
    }
  };

  const uniqueFromValues = extractUniqueValues(
    emailLogs,
    log => log?.tags?.['ses:sender-identity'] || log?.from,
    v => v.includes('@')
  );

  // 🚀 ALL AUTOCOMPLETE VALUES: Safe extraction for all filter fields
  const uniqueToValues = extractUniqueValues(
    emailLogs,
    log => (Array.isArray(log?.to) ? log.to.join(', ') : log?.to || ''),
    v => v.includes('@')
  );

  const uniqueSubjects = extractUniqueValues(emailLogs, log => log?.subject);

  const uniqueCampaignIds = extractUniqueValues(emailLogs, log => log?.campaignId);

  const uniqueMessageIds = extractUniqueValues(emailLogs, log => log?.messageId);

  const uniqueConfigurationSets = extractUniqueValues(
    emailLogs,
    log => log?.tags?.['ses:configuration-set']
  );

  const uniqueIpAddresses = extractUniqueValues(emailLogs, log => log?.ipAddress);

  const uniqueUserAgents = extractUniqueValues(emailLogs, log => {
    if (!log?.userAgent) return '';
    // Extract browser name for cleaner display
    if (log.userAgent.includes('Chrome')) return 'Chrome';
    if (log.userAgent.includes('Firefox')) return 'Firefox';
    if (log.userAgent.includes('Safari')) return 'Safari';
    if (log.userAgent.includes('Edge')) return 'Edge';
    if (log.userAgent.includes('Gmail')) return 'Gmail';
    if (log.userAgent.includes('Outlook')) return 'Outlook';
    return log.userAgent.split(' ')[0]; // First word
  });

  const uniqueRecipientIsps = extractUniqueValues(
    emailLogs,
    log => log?.tags?.['ses:recipient-isp']
  );

  const uniqueSourceIps = extractUniqueValues(emailLogs, log => log?.tags?.['ses:source-ip']);

  // Extract unique device/client combinations for filtering
  const uniqueDeviceClients = Array.from(
    new Set(
      emailLogs
        .map(log => {
          if (!log.userAgent) return '';

          // Determine device type
          const isMobile =
            log.userAgent.includes('Mobile') ||
            log.userAgent.includes('Android') ||
            log.userAgent.includes('iPhone') ||
            log.userAgent.includes('iPad');
          const deviceType = isMobile ? 'Mobile' : 'Desktop';

          // Determine client
          let clientType = 'Other';
          if (log.userAgent.includes('Chrome')) clientType = 'Chrome';
          else if (log.userAgent.includes('Firefox')) clientType = 'Firefox';
          else if (log.userAgent.includes('Safari')) clientType = 'Safari';
          else if (log.userAgent.includes('Edge')) clientType = 'Edge';
          else if (log.userAgent.includes('Gmail')) clientType = 'Gmail';
          else if (log.userAgent.includes('Outlook')) clientType = 'Outlook';

          return `${deviceType} ${clientType}`;
        })
        .filter(v => v && v.length > 0 && v !== 'Other')
    )
  ).sort();

  // Also add individual device types and client types for easier filtering
  const uniqueDeviceTypes = ['Mobile', 'Desktop'];
  const uniqueClientTypes = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Gmail', 'Outlook'];
  const combinedDeviceClientOptions = [
    ...uniqueDeviceTypes,
    ...uniqueClientTypes,
    ...uniqueDeviceClients,
  ].sort();

  // Handle column sort
  const handleSort = (column: keyof EmailLog) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Sort logs client-side (since API doesn't support sorting yet)
  const sortedLogs = [...emailLogs].sort((a, b) => {
    let aValue = a[sortColumn];
    let bValue = b[sortColumn];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // Handle date sorting
    if (sortColumn === 'timestamp' || sortColumn === 'createdAt' || sortColumn === 'updatedAt') {
      aValue = new Date(a[sortColumn] as string).getTime();
      bValue = new Date(b[sortColumn] as string).getTime();
    }

    // Handle string sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    // Handle number sorting
    const numA = Number(aValue);
    const numB = Number(bValue);
    return sortDirection === 'asc' ? numA - numB : numB - numA;
  });

  // Get status icon and color
  const getStatusIcon = (eventType: string) => {
    switch (eventType) {
      case 'send':
        return <Send className="w-4 h-4 text-gray-600" />;
      case 'delivery':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'open':
        return <Eye className="w-4 h-4 text-blue-600" />;
      case 'click':
        return <MousePointer className="w-4 h-4 text-purple-600" />;
      case 'bounce':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'complaint':
        return <AlertTriangle className="w-4 h-4 text-orange-600" />;
      default:
        return <Mail className="w-4 h-4 text-gray-600" />;
    }
  };

  // Sortable header component
  const SortableHeader = ({
    column,
    children,
    align = 'left',
  }: {
    column: keyof EmailLog;
    children: React.ReactNode;
    align?: 'left' | 'right';
  }) => (
    <th
      className={`px-6 py-3 text-${align} text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors select-none`}
      onClick={() => handleSort(column)}
    >
      <div className={`flex items-center gap-2 ${align === 'right' ? 'justify-end' : ''}`}>
        {children}
        {sortColumn === column &&
          (sortDirection === 'asc' ? (
            <ArrowUp className="w-4 h-4" />
          ) : (
            <ArrowDown className="w-4 h-4" />
          ))}
      </div>
    </th>
  );

  const eventTypeOptions = [
    'send',
    'delivery',
    'bounce',
    'complaint',
    'open',
    'click',
    'reject',
    'renderingFailure',
  ];
  const statusOptions = [
    'sent',
    'delivered',
    'bounced',
    'complained',
    'opened',
    'clicked',
    'failed',
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText className="w-7 h-7 text-indigo-600" />
              Email Logs
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Detailed view of all email events and interactions
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => refetchLogs()}
              className="btn btn-secondary flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button className="btn btn-primary flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Date Range & Filters */}
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          <div className="flex flex-col gap-1">
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              💡 Tip: Server-side pagination handles large datasets efficiently
            </p>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          <button
            onClick={() => {
              // Force cache clear and fresh data
              localStorage.removeItem('emailLogs_showDuplicates');
              setShowDuplicates(false);
              refetchLogs();
            }}
            className="btn btn-primary flex items-center gap-2"
            title="Clear cache and force fresh deduplicated data"
          >
            <RefreshCw className="w-4 h-4" />
            Force Refresh
          </button>

          {/* Quick Clear All Filters - Fixes the "No email logs found" issue */}
          {(eventTypeFilter ||
            statusFilter ||
            subjectFilter ||
            campaignIdFilter ||
            fromFilter ||
            toFilter ||
            ipAddressFilter ||
            userAgentFilter ||
            configurationSetFilter ||
            recipientIspFilter ||
            sourceIpFilter ||
            messageIdFilter ||
            deviceClientFilter) && (
            <button
              onClick={() => {
                // Clear ALL filters that might be hiding results
                setEventTypeFilter('');
                setStatusFilter('');
                setSubjectFilter('');
                setCampaignIdFilter('');
                setFromFilter('');
                setToFilter('');
                setIpAddressFilter('');
                setUserAgentFilter('');
                setConfigurationSetFilter('');
                setRecipientIspFilter('');
                setSourceIpFilter('');
                setMessageIdFilter('');
                setDeviceClientFilter('');
                // Clear localStorage for these filters
                localStorage.removeItem('emailLogs_subjectFilter');
                localStorage.removeItem('emailLogs_campaignIdFilter');
              }}
              className="btn btn-warning flex items-center gap-2"
              title="Clear all active filters to show all email logs"
            >
              <XCircle className="w-4 h-4" />
              Clear All Filters
            </button>
          )}

          {/* Show Duplicates Toggle */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={showDuplicates}
                onChange={e => setShowDuplicates(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show Duplicates
            </label>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && (
          <div className="mt-4 p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Primary Filters Row */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Primary Filters
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Event Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Event Type
                  </label>
                  <select
                    value={eventTypeFilter}
                    onChange={e => setEventTypeFilter(e.target.value)}
                    className="input"
                  >
                    <option value="">All Types</option>
                    {eventTypeOptions.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={e => setStatusFilter(e.target.value)}
                    className="input"
                  >
                    <option value="">All Statuses</option>
                    {statusOptions.map(status => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subject Filter */}
                <CustomAutocomplete
                  label="Subject Contains"
                  value={subjectFilter}
                  onChange={setSubjectFilter}
                  options={uniqueSubjects}
                  placeholder="Search subject..."
                />

                {/* Campaign ID Filter */}
                <CustomAutocomplete
                  label="Campaign ID"
                  value={campaignIdFilter}
                  onChange={setCampaignIdFilter}
                  options={uniqueCampaignIds}
                  placeholder="Select campaign..."
                />
              </div>
            </div>

            {/* Email Addresses Row */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email Addresses
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* From Filter */}
                <CustomAutocomplete
                  label="From (Sender)"
                  value={fromFilter}
                  onChange={setFromFilter}
                  options={uniqueFromValues}
                  placeholder="sender@domain.com"
                />

                {/* To Filter */}
                <CustomAutocomplete
                  label="To (Recipient)"
                  value={toFilter}
                  onChange={setToFilter}
                  options={uniqueToValues}
                  placeholder="recipient@domain.com"
                />

                {/* Message ID Filter */}
                <CustomAutocomplete
                  label="Message ID"
                  value={messageIdFilter}
                  onChange={setMessageIdFilter}
                  options={uniqueMessageIds}
                  placeholder="Select message ID..."
                />

                {/* Configuration Set Filter */}
                <CustomAutocomplete
                  label="Configuration Set"
                  value={configurationSetFilter}
                  onChange={setConfigurationSetFilter}
                  options={uniqueConfigurationSets}
                  placeholder="delivery-tracking"
                />
              </div>
            </div>

            {/* Technical Details Row */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Technical Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Device/Client Filter */}
                <CustomAutocomplete
                  label="Device/Client"
                  value={deviceClientFilter}
                  onChange={setDeviceClientFilter}
                  options={combinedDeviceClientOptions}
                  placeholder="Mobile, Desktop, Chrome..."
                />

                {/* User IP Address Filter */}
                <CustomAutocomplete
                  label="User IP Address"
                  value={ipAddressFilter}
                  onChange={setIpAddressFilter}
                  options={uniqueIpAddresses}
                  placeholder="66.249.93.36"
                />

                {/* User Agent Filter */}
                <CustomAutocomplete
                  label="User Agent (Full)"
                  value={userAgentFilter}
                  onChange={setUserAgentFilter}
                  options={uniqueUserAgents}
                  placeholder="Chrome, Firefox, Gmail..."
                />

                {/* Source IP Filter */}
                <CustomAutocomplete
                  label="Source IP (SES)"
                  value={sourceIpFilter}
                  onChange={setSourceIpFilter}
                  options={uniqueSourceIps}
                  placeholder="134.209.29.68"
                />

                {/* Recipient ISP Filter */}
                <CustomAutocomplete
                  label="Recipient ISP"
                  value={recipientIspFilter}
                  onChange={setRecipientIspFilter}
                  options={uniqueRecipientIsps}
                  placeholder="Gmail, Outlook, Yahoo..."
                />
              </div>
            </div>

            {/* Filter Summary & Clear */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing <strong>{emailLogs.length}</strong> of <strong>{pagination.total}</strong>{' '}
                email logs
                {(eventTypeFilter ||
                  statusFilter ||
                  subjectFilter ||
                  campaignIdFilter ||
                  fromFilter ||
                  toFilter ||
                  ipAddressFilter ||
                  userAgentFilter ||
                  configurationSetFilter ||
                  recipientIspFilter ||
                  sourceIpFilter ||
                  messageIdFilter ||
                  deviceClientFilter) && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                    Filtered
                  </span>
                )}
              </div>
              {(eventTypeFilter ||
                statusFilter ||
                subjectFilter ||
                campaignIdFilter ||
                fromFilter ||
                toFilter ||
                ipAddressFilter ||
                userAgentFilter ||
                configurationSetFilter ||
                recipientIspFilter ||
                sourceIpFilter ||
                messageIdFilter ||
                deviceClientFilter) && (
                <button
                  onClick={() => {
                    setEventTypeFilter('');
                    setStatusFilter('');
                    setSubjectFilter('');
                    setCampaignIdFilter('');
                    setFromFilter('');
                    setToFilter('');
                    setIpAddressFilter('');
                    setUserAgentFilter('');
                    setConfigurationSetFilter('');
                    setRecipientIspFilter('');
                    setSourceIpFilter('');
                    setMessageIdFilter('');
                    setDeviceClientFilter('');
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm font-medium"
                >
                  <XCircle className="w-4 h-4" />
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Date Range Limitation Warning */}
        {emailLogsData?.meta?.wasDateRangeLimited && (
          <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
                  Date Range Limited
                </h4>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {emailLogsData.meta.warning}
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  Effective range:{' '}
                  {new Date(emailLogsData.meta.effectiveDateRange.startDate).toLocaleDateString()} -{' '}
                  {new Date(emailLogsData.meta.effectiveDateRange.endDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Email Logs Table */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-800">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Email Event Log
            </h2>
          </div>

          {loadingLogs ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-4">Loading email logs...</p>
            </div>
          ) : emailLogs.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No email logs found.</p>
              {eventTypeFilter || statusFilter || subjectFilter || campaignIdFilter ? (
                <div className="mt-4">
                  <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
                    📊 Active filters may be hiding results. Try clearing them.
                  </p>
                  <button
                    onClick={() => {
                      setEventTypeFilter('');
                      setStatusFilter('');
                      setSubjectFilter('');
                      setCampaignIdFilter('');
                      setFromFilter('');
                      setToFilter('');
                      setIpAddressFilter('');
                      setUserAgentFilter('');
                      setConfigurationSetFilter('');
                      setRecipientIspFilter('');
                      setSourceIpFilter('');
                      setMessageIdFilter('');
                      setDeviceClientFilter('');
                    }}
                    className="btn btn-primary"
                  >
                    Clear All Filters
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-2">
                  Try expanding the date range or check your data source configuration.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <SortableHeader column="timestamp">Time</SortableHeader>
                    <SortableHeader column="eventType">Event</SortableHeader>
                    <SortableHeader column="status">Status</SortableHeader>
                    <SortableHeader column="from">Sender</SortableHeader>
                    <SortableHeader column="to">To</SortableHeader>
                    <SortableHeader column="subject">Subject</SortableHeader>
                    <SortableHeader column="userAgent">Device/Client</SortableHeader>
                    <SortableHeader column="ipAddress">Location</SortableHeader>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {sortedLogs.map(log => (
                    <tr
                      key={log._id}
                      onClick={() => setSelectedEmailLog(log)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {new Date(log.timestamp).toLocaleDateString()}
                          </div>
                          <div className="text-gray-500">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(log.eventType)}
                          <span className="text-sm font-medium">{log.eventType}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            log.status === 'delivered'
                              ? 'bg-green-100 text-green-800'
                              : log.status === 'opened'
                              ? 'bg-blue-100 text-blue-800'
                              : log.status === 'clicked'
                              ? 'bg-purple-100 text-purple-800'
                              : log.status === 'bounced'
                              ? 'bg-red-100 text-red-800'
                              : log.status === 'sent'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {/* Show SES sender identity if available, otherwise fall back to from field */}
                          {log.tags?.['ses:sender-identity'] || log.from}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {log.to.join(', ')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {log.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {log.userAgent ? (
                            <span className="flex items-center gap-1">
                              {log.userAgent.includes('Mobile') ||
                              log.userAgent.includes('Android') ||
                              log.userAgent.includes('iPhone')
                                ? '📱'
                                : '💻'}
                              <span className="truncate">
                                {log.userAgent.includes('Chrome')
                                  ? 'Chrome'
                                  : log.userAgent.includes('Firefox')
                                  ? 'Firefox'
                                  : log.userAgent.includes('Safari')
                                  ? 'Safari'
                                  : log.userAgent.includes('Edge')
                                  ? 'Edge'
                                  : 'Other'}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {log.ipAddress ? (
                            <span className="flex items-center gap-1">
                              🌐
                              <span className="font-mono text-xs">{log.ipAddress}</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing <strong>{pagination.skip + 1}</strong> to{' '}
                    <strong>
                      {Math.min(pagination.skip + pagination.limit, pagination.total)}
                    </strong>{' '}
                    of <strong>{pagination.total}</strong> email logs
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-3 py-1 text-sm rounded ${
                              currentPage === pageNum
                                ? 'bg-indigo-600 text-white'
                                : 'border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Email Log Details Modal */}
      <EmailLogDetailsModal emailLog={selectedEmailLog} onClose={() => setSelectedEmailLog(null)} />
    </div>
  );
};

export default EmailLogsPage;
